import { CircularProgress, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import useSelectedSector from "hooks/useSelectedSector";
import TravelOfferService from "services/TravelOfferService";
import { TravelCard } from "components/partials/TravelCard/TravelCard";
import { GoogleMap, Marker, OverlayView } from "@react-google-maps/api";
import React, { useCallback, useRef, useState } from "react";
import { TravelOffer } from "types/TravelOffer";
import TravelOfferDetailsModal from "./components/TravelOfferDetailsModal";
import { Refresh } from "@mui/icons-material";
import { Column } from "components/common/Layout/Column";
import { Row } from "components/common/Layout/Row";
import ActiveTransportDrivers from "./components/ActiveTransportDrivers/ActiveTransportDrivers";
import { TransportDriver } from "types/TransportDriver";

export const ActiveTravelOffersList = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const { selectedSectorId } = useSelectedSector();
  const {
    data: travelOffers,
    refetch,
    isFetching,
  } = TravelOfferService.useGetTravelOffersActiveBySectorQuery(
    { sectorId: selectedSectorId },
    { refetchOnMountOrArgChange: true },
  );

  const [selectedTravelOffer, setSelectedTravelOffer] = useState<TravelOffer | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [drivers, setDrivers] = useState<TransportDriver[]>([]);

  const handleSelectOffer = useCallback(
    (travelOffer: TravelOffer) => {
      if (travelOffer.id === selectedTravelOffer?.id) {
        setSelectedTravelOffer(null);
      } else {
        setSelectedTravelOffer(travelOffer);
      }
    },
    [selectedTravelOffer],
  );

  const handleOpenTravelOffer = useCallback((travelOffer: TravelOffer) => {
    setSelectedTravelOffer(travelOffer);
    setModalOpen(true);
  }, []);

  const onDriversFetched = useCallback((drivers: TransportDriver[]) => {
    setDrivers(drivers);
  }, []);

  return (
    <Column sx={{ flex: 1 }}>
      <ActiveTransportDrivers onDriversFetched={onDriversFetched} />

      {/* ---------- travel cards ---------- */}
      <Column sx={{ flex: 1, overflowX: "auto" }}>
        <Row alignItems="center" sx={{ pb: 1, px: 1 }}>
          <Typography variant="h6">Solicitudes de viaje</Typography>
          <IconButton onClick={() => refetch()} disabled={isFetching}>
            <Refresh />
          </IconButton>
        </Row>

        {isFetching ? (
          <Row padding={2}>
            <CircularProgress />
          </Row>
        ) : (
          <Row
            /* stack cards on XS, horizontal scroll otherwise                 */
            sx={{
              gap: 2,
              flexWrap: isXs ? "wrap" : "nowrap",
              overflowX: isXs ? "visible" : "auto",
              px: 1,
            }}
          >
            {travelOffers?.map((travelOffer) => (
              <TravelCard
                key={travelOffer.id}
                travelOffer={travelOffer}
                onClick={handleSelectOffer}
                isSelected={travelOffer.id === selectedTravelOffer?.id}
                onEyeClick={handleOpenTravelOffer}
                /* full-width card on phones                                  */
              />
            ))}
          </Row>
        )}
      </Column>

      {/* ---------- map ---------- */}
      <GoogleMap
        onLoad={(m) => {
          mapRef.current = m;
        }}
        center={{ lat: 14.040564740651831, lng: -88.9359117588296 }}
        zoom={15}
        mapContainerStyle={{
          height: isXs ? "300px" : "700px",
          width: "100%",
        }}
      >
        {selectedTravelOffer && (
          <>
            <Marker
              position={{
                lat: selectedTravelOffer.origin.coordinates[1],
                lng: selectedTravelOffer.origin.coordinates[0],
              }}
              label={{ text: "A", color: "white", fontSize: "16px", fontWeight: "bold" }}
            />
            <Marker
              position={{
                lat: selectedTravelOffer.destination.coordinates[1],
                lng: selectedTravelOffer.destination.coordinates[0],
              }}
              label={{ text: "B", color: "white", fontSize: "16px", fontWeight: "bold" }}
            />
          </>
        )}

        {drivers.map((driver) => {
          const { lastLocation, currentVehicle, id } = driver;
          const iconUrl = currentVehicle?.vehicleType?.icon;

          return (
            <React.Fragment key={id}>
              <Marker
                position={{
                  lat: lastLocation.coordinates[1],
                  lng: lastLocation.coordinates[0],
                }}
                icon={
                  iconUrl
                    ? {
                        url: iconUrl,
                        scaledSize: new window.google.maps.Size(32, 32),
                      }
                    : undefined
                }
                title={driver.user.name}
              />
              <OverlayView
                position={{
                  lat: lastLocation.coordinates[1],
                  lng: lastLocation.coordinates[0],
                }}
                mapPaneName={OverlayView.FLOAT_PANE}
              >
                <div
                  style={{
                    padding: "2px 4px",
                    borderRadius: "4px",
                    whiteSpace: "nowrap",
                    fontSize: "14px",
                    fontWeight: "bold",
                    background: "white",
                  }}
                >
                  {driver.user.name}
                </div>
              </OverlayView>
            </React.Fragment>
          );
        })}
      </GoogleMap>

      <TravelOfferDetailsModal open={modalOpen} travelOffer={selectedTravelOffer} onClose={() => setModalOpen(false)} />
    </Column>
  );
};
